import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import PubBadge from "../../components/PubBadge";
import { toast } from "sonner";
import { Star, ExternalLink } from "lucide-react";

export default function VendorDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get(`/admin/vendors/${id}`);
    setV(r.data);
    setForm({
      name: r.data.name || "",
      type: r.data.type || "",
      website: r.data.website || "",
      instagram: r.data.instagram || "",
      email: r.data.email || "",
      contact_name: r.data.contact_name || "",
      is_partner: !!r.data.is_partner,
      is_approved: !!r.data.is_approved,
    });
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/vendors/${id}`, form);
      toast.success("Saved");
      load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const togglePartner = async () => {
    const next = !form.is_partner;
    setForm((f) => ({ ...f, is_partner: next }));
    await api.patch(`/admin/vendors/${id}`, { is_partner: next });
    toast.success(next ? "Marked as active partner" : "Partner status removed");
    load();
  };

  const remove = async () => {
    if (!window.confirm("Delete vendor? This cannot be undone.")) return;
    await api.delete(`/admin/vendors/${id}`);
    toast.success("Deleted");
    navigate("/admin/vendors");
  };

  if (!v) return <div className="p-8 text-sm text-[#5c5852]">Loading…</div>;

  return (
    <div className="p-6 lg:p-10" data-testid="vendor-detail-page">
      <Link to="/admin/vendors" className="btw-overline hover:underline">← All vendors</Link>

      <div className="mt-3 mb-8 flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-3">
          {v.is_partner && (
            <Star size={28} className="text-[#876a4f] fill-[#c19872]" data-testid="partner-star" />
          )}
          <div>
            <p className="btw-overline mb-1">
              {v.type || "Vendor"} · {v.event_count} feature{v.event_count !== 1 ? "s" : ""}
            </p>
            <h1 className="font-serif text-4xl">{v.name}</h1>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={togglePartner}
            data-testid="toggle-partner"
            className="text-xs px-3 py-2 border border-[#c19872] text-[#876a4f] hover:bg-[#e2d3c0]/40 inline-flex items-center gap-1"
          >
            <Star size={12} fill={form.is_partner ? "#c19872" : "none"} />
            {form.is_partner ? "Active partner" : "Mark as partner"}
          </button>
          <button
            onClick={remove}
            data-testid="vendor-delete"
            className="text-xs px-3 py-2 border border-[#b35c5c] text-[#b35c5c] hover:bg-[#b35c5c]/10"
          >
            Delete
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <section className="bg-white border border-[#dcd3c6] p-5" data-testid="vendor-edit">
          <h2 className="btw-overline mb-4">Vendor info</h2>
          <div className="space-y-4">
            <Field testid="vendor-name" label="Business name" value={form.name} onChange={(x) => setForm({ ...form, name: x })} />
            <Field testid="vendor-type" label="Type / category" value={form.type} onChange={(x) => setForm({ ...form, type: x })} />
            <Field testid="vendor-website" label="Website" value={form.website} onChange={(x) => setForm({ ...form, website: x })} />
            <Field testid="vendor-instagram" label="Instagram (no @)" value={form.instagram} onChange={(x) => setForm({ ...form, instagram: x.replace(/^@/, "") })} />
            <Field testid="vendor-email" label="Email" value={form.email} onChange={(x) => setForm({ ...form, email: x })} />
            <Field testid="vendor-contact" label="Contact name" value={form.contact_name} onChange={(x) => setForm({ ...form, contact_name: x })} />
            <label className="flex items-center gap-2 text-sm text-[#5c5852] cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.is_approved}
                onChange={(e) => setForm({ ...form, is_approved: e.target.checked })}
                data-testid="vendor-approved-toggle"
                className="h-4 w-4 accent-[#c19872]"
              />
              Verified (cleaned & ready to publish)
            </label>
          </div>
          <button onClick={save} disabled={saving} data-testid="vendor-save" className="btw-btn mt-6 text-xs px-4 py-2">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </section>

        <section className="bg-white border border-[#dcd3c6] p-5" data-testid="vendor-features">
          <h2 className="btw-overline mb-4">Featured in ({v.features.length})</h2>
          {v.features.length === 0 ? (
            <p className="text-sm italic text-[#5c5852]">Not featured in any event yet.</p>
          ) : (
            <div className="divide-y divide-[#dcd3c6]/60">
              {v.features.map((f) => (
                <div key={f.event_id} className="py-3 first:pt-0 last:pb-0" data-testid={`feature-${f.event_id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <Link to={`/admin/events/${f.event_id}`} className="font-medium hover:text-[#876a4f]">
                        {f.event_name}
                      </Link>
                      <div className="text-xs text-[#5c5852] mt-1 flex flex-wrap gap-2">
                        <PubBadge pub={f.publication} />
                        <span>·</span>
                        <span>{f.vendor_type}</span>
                        <span>·</span>
                        <span>{f.status}</span>
                        {f.is_live && <span className="text-[#768c6a]">· LIVE</span>}
                      </div>
                    </div>
                    {f.feature_url && (
                      <a
                        href={f.feature_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs inline-flex items-center gap-1 text-[#876a4f] hover:underline"
                        data-testid={`feature-link-${f.event_id}`}
                      >
                        View <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, testid }) => (
  <div>
    <label className="btw-overline block mb-1">{label}</label>
    <input
      data-testid={testid}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-transparent border-b border-[#dcd3c6] focus:border-[#c19872] outline-none py-2 text-sm"
    />
  </div>
);
